import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { Borrador, RenglonBorrador, RenglonGuardado } from '@/lib/supabase/tipos-bd'

/**
 * El borrador de cotización.
 *
 * Se arma en la matriz y World Office no se entera hasta que alguien decide
 * generarlo. Hasta entonces se puede cerrar la pantalla, volver mañana y
 * seguir donde se quedó.
 */

/** Abre el borrador de una orden. Llamarla dos veces devuelve el mismo. */
export function useAbrirBorrador() {
  return useMutation({
    mutationFn: async ({
      ordenServicioId,
      adicional = false,
    }: {
      ordenServicioId: string
      /** Cuelga de una obra en curso: no reemplaza a la cotización que ya está. */
      adicional?: boolean
    }): Promise<string> => {
      const { data, error } = await supabase.rpc('fn_abrir_borrador', {
        p_orden_servicio_id: ordenServicioId,
        p_adicional: adicional,
      })

      if (error) throw new Error(mensajeDeError(error))

      return data as string
    },
  })
}

/**
 * Lista vacía compartida.
 *
 * Un `?? []` en el retorno crea un arreglo nuevo en cada render. Si ese
 * arreglo es dependencia de un useEffect que llama a setState, el efecto se
 * dispara para siempre. Con una constante la referencia no cambia.
 */
const SIN_RENGLONES: RenglonGuardado[] = []

/** Los renglones ya guardados, para retomar donde se quedó. */
export function useRenglonesGuardados(cotizacionId: string | null) {
  const consulta = useQuery({
    queryKey: ['borrador-renglones', cotizacionId],
    enabled: Boolean(cotizacionId),
    queryFn: async (): Promise<RenglonGuardado[]> => {
      const { data, error } = await supabase
        .from('cotizacion_renglones')
        .select('*')
        .eq('cotizacion_id', cotizacionId!)
        .order('orden')

      if (error) throw new Error(mensajeDeError(error))

      return (data ?? []) as unknown as RenglonGuardado[]
    },
  })

  return {
    guardados: consulta.data ?? SIN_RENGLONES,
    cargando: consulta.isLoading,
    // 0325: ya llegó lo guardado de ESTA cotización. Hasta entonces no se puede
    // editar ni autoguardar: se guardaría una lista vacía encima.
    listos: consulta.isSuccess,
  }
}

/** Los totales del borrador, calculados por la base. */
export function useTotalesBorrador(cotizacionId: string | null) {
  const consulta = useQuery({
    queryKey: ['borrador-totales', cotizacionId],
    enabled: Boolean(cotizacionId),
    queryFn: async (): Promise<Borrador | null> => {
      const { data, error } = await supabase
        .from('vista_borrador')
        .select('*')
        .eq('cotizacion_id', cotizacionId!)
        .maybeSingle()

      if (error) throw new Error(mensajeDeError(error))

      return (data ?? null) as unknown as Borrador | null
    },
  })

  return consulta.data ?? null
}

/**
 * Guarda los renglones.
 *
 * Se manda la lista completa y la base reemplaza. Es más simple que ir
 * sincronizando alta por alta, y no deja estados a medias si algo falla.
 */
export function useGuardarBorrador() {
  const clienteConsultas = useQueryClient()

  return useMutation({
    mutationFn: async ({
      cotizacionId,
      renglones,
      vaciar = false,
    }: {
      cotizacionId: string
      renglones: RenglonBorrador[]
      /** «Empezar de cero»: la única forma de dejar vacío un borrador (0325). */
      vaciar?: boolean
    }): Promise<number> => {
      const { data, error } = await supabase.rpc('fn_guardar_borrador', {
        p_cotizacion_id: cotizacionId,
        p_renglones: renglones,
        ...(vaciar ? { p_vaciar: true } : {}),
      })

      if (error) throw new Error(mensajeDeError(error))

      return data as number
    },
    onSuccess: async (_dato, variables) => {
      await clienteConsultas.invalidateQueries({
        queryKey: ['borrador-renglones', variables.cotizacionId],
      })
      await clienteConsultas.invalidateQueries({
        queryKey: ['borrador-totales', variables.cotizacionId],
      })
    },
  })
}

/** Manda el borrador a World Office. */
export function useGenerarCotizacion() {
  const clienteConsultas = useQueryClient()

  return useMutation({
    // Devuelve null cuando la cotización se queda en la aplicación: la de una
    // orden ya no viaja a World Office al armarla, viaja cuando gerencia la
    // aprueba. Declararlo string a secas escondía ese caso.
    mutationFn: async ({
      cotizacionId,
      descripcion = null,
    }: {
      cotizacionId: string
      /** 0302: la descripción del trabajo; va a la orden y de ahí a World Office. */
      descripcion?: string | null
    }): Promise<string | null> => {
      const { data, error } = await supabase.rpc('fn_generar_cotizacion', {
        p_cotizacion_id: cotizacionId,
        p_descripcion: descripcion?.trim() || null,
      })

      if (error) throw new Error(mensajeDeError(error))

      return (data as string | null) ?? null
    },
    onSuccess: async () => {
      await clienteConsultas.invalidateQueries({ queryKey: ['ordenes-servicio'] })
      await clienteConsultas.invalidateQueries({ queryKey: ['pendientes-de-decision'] })
    },
  })
}

/**
 * Prepara una cotización ya generada para modificarla.
 *
 * Las armadas desde la app ya tienen sus renglones en la matriz; las que se
 * hicieron en World Office viven en el reflejo de solo lectura y hay que
 * traerlas primero.
 */
export function usePrepararModificacion() {
  const clienteConsultas = useQueryClient()

  return useMutation({
    mutationFn: async (cotizacionId: string): Promise<number> => {
      const { data, error } = await supabase.rpc('fn_preparar_modificacion', {
        p_cotizacion_id: cotizacionId,
      })

      if (error) throw new Error(mensajeDeError(error))

      return data as number
    },
    onSuccess: async (_dato, cotizacionId) => {
      await clienteConsultas.invalidateQueries({
        queryKey: ['borrador-renglones', cotizacionId],
      })
    },
  })
}

/** Manda los renglones nuevos a World Office. El número no cambia. */
export function useModificarCotizacion() {
  const clienteConsultas = useQueryClient()

  return useMutation({
    mutationFn: async ({
      cotizacionId,
      renglones,
      motivo,
      idTerceroNuevo,
    }: {
      cotizacionId: string
      renglones: RenglonBorrador[]
      motivo?: string
      /** Cliente nuevo, solo para cotizaciones sin orden. */
      idTerceroNuevo?: number | null
      /*
       * Devuelve el id de la solicitud… o NULL cuando la cotización todavía no
       * está en World Office y se corrigió solo aquí (0189). Quien llame tiene
       * que distinguirlo: si no, se queda esperando una respuesta de allá que
       * nadie va a mandar.
       */
    }): Promise<string | null> => {
      const { data, error } = await supabase.rpc('fn_modificar_cotizacion', {
        p_cotizacion_id: cotizacionId,
        p_renglones: renglones,
        p_motivo: motivo ?? null,
        p_id_tercero_nuevo: idTerceroNuevo ?? null,
      })

      if (error) throw new Error(mensajeDeError(error))

      return (data as string | null) ?? null
    },
    onSuccess: async () => {
      await clienteConsultas.invalidateQueries({ queryKey: ['ordenes-servicio'] })
      await clienteConsultas.invalidateQueries({ queryKey: ['cotizaciones-wo'] })
    },
  })
}

/** Rechaza la cotización y la anula también en World Office. */
export function useAnularCotizacion() {
  const clienteConsultas = useQueryClient()

  return useMutation({
    mutationFn: async ({
      cotizacionId,
      motivo,
    }: {
      cotizacionId: string
      motivo: string
    }): Promise<string | null> => {
      const { data, error } = await supabase.rpc('fn_anular_cotizacion', {
        p_cotizacion_id: cotizacionId,
        p_motivo: motivo,
      })

      if (error) throw new Error(mensajeDeError(error))

      return data as string | null
    },
    onSuccess: async () => {
      await clienteConsultas.invalidateQueries({ queryKey: ['ordenes-servicio'] })
      await clienteConsultas.invalidateQueries({ queryKey: ['cotizaciones-wo'] })
      await clienteConsultas.invalidateQueries({ queryKey: ['pendientes-de-decision'] })
    },
  })
}

/** Descarta un borrador que no se va a usar. */
export function useDescartarBorrador() {
  const clienteConsultas = useQueryClient()

  return useMutation({
    mutationFn: async (cotizacionId: string): Promise<void> => {
      const { error } = await supabase.rpc('fn_descartar_borrador', {
        p_cotizacion_id: cotizacionId,
      })

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: async () => {
      await clienteConsultas.invalidateQueries({ queryKey: ['ordenes-servicio'] })
    },
  })
}

/** 0318: Las herramientas que el técnico estimó necesarias para el trabajo. */
export function useHerramientasCotizacion(cotizacionId: string | null) {
  const consulta = useQuery({
    queryKey: ['cotizacion-herramientas', cotizacionId],
    enabled: Boolean(cotizacionId),
    queryFn: async (): Promise<Record<string, number>> => {
      const { data, error } = await supabase
        .from('cotizaciones')
        .select('herramientas_requeridas')
        .eq('id', cotizacionId!)
        .maybeSingle()

      if (error) return {}
      return ((data as { herramientas_requeridas?: Record<string, number> | null } | null)
        ?.herramientas_requeridas ?? {}) as Record<string, number>
    },
  })

  return { herramientas: consulta.data ?? {}, cargando: consulta.isLoading }
}

/** 0318: Guarda las herramientas requeridas de la cotización. */
export function useGuardarHerramientasCotizacion() {
  const clienteConsultas = useQueryClient()

  return useMutation({
    mutationFn: async ({
      cotizacionId,
      herramientas,
    }: {
      cotizacionId: string
      herramientas: Record<string, number>
    }) => {
      const { error } = await supabase.rpc('fn_guardar_herramientas_cotizacion', {
        p_cotizacion_id: cotizacionId,
        p_herramientas: herramientas,
      })

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: async (_dato, variables) => {
      await clienteConsultas.invalidateQueries({
        queryKey: ['cotizacion-herramientas', variables.cotizacionId],
      })
    },
  })
}
